import { ChainId } from '@pancakeswap/chains'
import { formatBigInt } from '@pancakeswap/utils/formatBalance'
import { erc404ABI } from 'config/abi/erc404'
import { nftMarketABI } from 'config/abi/nftMarket'
import { NUTS_COLLECTION } from 'config/constants/nftsCollections'
import { getNftMarketAddress } from 'utils/addressHelpers'
import { publicClient } from 'utils/wagmi'
import type { IndexMap, IndexSnapshot } from 'pages/api/nuts/index-map'
import { parseAbiItem, zeroAddress } from 'viem'
import { CollectionMarketDataBaseFields, TokenMarketData } from './types'

// ponytail: single hard-coded NUTS collection, on-chain reads replace the dead subgraph.
// Everything here is memoized per page load for TTL ms; no cross-tab cache.
const TTL = 30_000
const memo = <T>(fn: () => Promise<T>) => {
  let p: Promise<T> | undefined
  let at = 0
  return () => {
    if (!p || Date.now() - at > TTL) {
      at = Date.now()
      p = fn().catch((e) => {
        p = undefined
        throw e
      })
    }
    return p
  }
}

const client = () => publicClient({ chainId: ChainId.PULSECHAIN })
const TRANSFER = parseAbiItem('event Transfer(address indexed from, address indexed to, uint256 indexed id)')

/**
 * imageIndex -> { id: live on-chain id, owner } for every live NUTS token.
 * The server snapshot is cached for hours, so replay NFT Transfer logs since its block to bring it current.
 */
export const getIndexMap = memo(async (): Promise<IndexMap> => {
  const res = await fetch('/api/nuts/index-map')
  if (!res.ok) throw new Error(`index-map ${res.status}`)
  const { block, map }: IndexSnapshot = await res.json()
  try {
    const logs = await client().getLogs({
      address: NUTS_COLLECTION,
      event: TRANSFER,
      fromBlock: BigInt(block) + 1n,
    })
    const minted = logs.filter((l) => l.args.from === zeroAddress).map((l) => l.args.id as bigint)
    const mintedIndex = new Map(minted.map((id, i) => [id, i]))
    const indexes = await getIndexes(minted)
    const indexOfId = new Map(Object.entries(map).map(([index, v]) => [v.id, index]))
    logs.forEach(({ args: { from, to, id } }) => {
      const key = String(id)
      if (from === zeroAddress) {
        // ids only ascend, so a fresh mint is the newest holder of its image index
        const index = indexes[mintedIndex.get(id as bigint) as number]
        if (map[index] && BigInt(map[index].id) > (id as bigint)) return
        map[index] = { id: key, owner: (to as string).toLowerCase() }
        indexOfId.set(key, index)
        return
      }
      const index = indexOfId.get(key)
      if (index === undefined || map[index]?.id !== key) return
      if (to === zeroAddress) delete map[index]
      else map[index] = { id: key, owner: (to as string).toLowerCase() }
    })
  } catch (e) {
    console.error('index-map log replay failed; using snapshot', e)
  }
  return map
})

export const getIndexes = async (realIds: bigint[]): Promise<string[]> => {
  if (!realIds.length) return []
  const r = await client().multicall({
    contracts: realIds.map((id) => ({
      address: NUTS_COLLECTION,
      abi: erc404ABI,
      functionName: 'GetIndex',
      args: [id],
    })),
    allowFailure: false,
  })
  return r.map((x) => x.toString())
}

/** every open ask on the market for NUTS; tokenId = on-chain id, otherId = image index */
export const getAllAsks = memo(async (): Promise<TokenMarketData[]> => {
  const market = getNftMarketAddress()
  const ids: bigint[] = []
  const asks: { seller: string; price: bigint }[] = []
  let cursor = 0n
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const [tokenIds, askInfo, next] = await client().readContract({
      address: market,
      abi: nftMarketABI,
      functionName: 'viewAsksByCollection',
      args: [NUTS_COLLECTION, cursor, 500n],
    })
    ids.push(...tokenIds)
    asks.push(...askInfo)
    if (tokenIds.length < 500 || next === 0n) break
    cursor = next
  }
  const indexes = await getIndexes(ids)
  return ids.map((id, i) => ({
    id: `${NUTS_COLLECTION.toLowerCase()}-${id}`,
    tokenId: id.toString(),
    otherId: indexes[i],
    collection: { id: NUTS_COLLECTION.toLowerCase() },
    currentSeller: asks[i].seller,
    currentAskPrice: formatBigInt(asks[i].price),
    isTradable: true,
  }))
})

/** minimal subgraph-shaped collection record built from the market contract */
export const getCollectionOnChain = memo(async (): Promise<CollectionMarketDataBaseFields> => {
  const [[addresses, infos], asks] = await Promise.all([
    client().readContract({
      address: getNftMarketAddress(),
      abi: nftMarketABI,
      functionName: 'viewCollections',
      args: [0n, 100n],
    }),
    getAllAsks(),
  ])
  const i = addresses.findIndex((a) => a.toLowerCase() === NUTS_COLLECTION.toLowerCase())
  const info = infos[i]
  return {
    id: NUTS_COLLECTION.toLowerCase() as any,
    name: '',
    symbol: '',
    active: true,
    totalTrades: '0',
    totalVolumePLS: '0',
    numberTokensListed: String(asks.length),
    creatorAddress: info?.creatorAddress ?? '',
    tradingFee: info ? (Number(info.tradingFee) / 100).toString() : '',
    creatorFee: info ? (Number(info.creatorFee) / 100).toString() : '',
    whitelistChecked: info?.whitelistChecker ?? '',
  }
})
